<template>
  <div class="mouse-follower" v-if="isEnabled">
    <div class="cursor-dot" ref="cursorDot"></div>
    <div class="cursor-ring" ref="cursorRing"></div>
    <!-- 水波纹扩散层 -->
    <div class="ripple-container" ref="rippleContainer">
      <div class="ripple" v-for="i in 3" :key="i" :style="{ animationDelay: (i - 1) * 0.6 + 's' }"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

// 是否启用（仅PC端）
const isEnabled = ref(false);
const cursorDot = ref(null);
const cursorRing = ref(null);
const rippleContainer = ref(null);

// 光标位置
let mouseX = 0;
let mouseY = 0;
let ringX = 0;
let ringY = 0;
let rippleX = 0;
let rippleY = 0;

// 动画帧ID
let animationId = null;

// 处理鼠标移动
const handleMouseMove = (e) => {
  mouseX = e.clientX;
  mouseY = e.clientY;
  
  // 小点直接跟随
  if (cursorDot.value) {
    cursorDot.value.style.left = mouseX + 'px';
    cursorDot.value.style.top = mouseY + 'px';
  }
};

// 平滑动画
const animate = () => {
  // 圆环缓慢跟随
  ringX += (mouseX - ringX) * 0.12;
  ringY += (mouseY - ringY) * 0.12;
  
  // 水波纹更慢跟随
  rippleX += (mouseX - rippleX) * 0.08;
  rippleY += (mouseY - rippleY) * 0.08;
  
  if (cursorRing.value) {
    cursorRing.value.style.left = ringX + 'px';
    cursorRing.value.style.top = ringY + 'px';
  }
  
  if (rippleContainer.value) {
    rippleContainer.value.style.left = rippleX + 'px';
    rippleContainer.value.style.top = rippleY + 'px';
  }
  
  animationId = requestAnimationFrame(animate);
};

// 处理悬停状态
const handleHover = (isHover) => {
  cursorDot.value?.classList.toggle('cursor-hover', isHover);
  cursorRing.value?.classList.toggle('cursor-hover', isHover);
  rippleContainer.value?.classList.toggle('cursor-hover', isHover);
};

// 处理点击状态
const handleMouseDown = () => {
  cursorDot.value?.classList.add('cursor-click');
  cursorRing.value?.classList.add('cursor-click');
};

const handleMouseUp = () => {
  cursorDot.value?.classList.remove('cursor-click');
  cursorRing.value?.classList.remove('cursor-click');
};

// 添加悬停监听
const addHoverListeners = () => {
  const hoverElements = document.querySelectorAll('a, button, [role="button"], .cursor-pointer, input, textarea, select');
  hoverElements.forEach(el => {
    el.addEventListener('mouseenter', () => handleHover(true));
    el.addEventListener('mouseleave', () => handleHover(false));
  });
};

// 移除悬停监听
const removeHoverListeners = () => {
  const hoverElements = document.querySelectorAll('a, button, [role="button"], .cursor-pointer, input, textarea, select');
  hoverElements.forEach(el => {
    el.removeEventListener('mouseenter', () => handleHover(true));
    el.removeEventListener('mouseleave', () => handleHover(false));
  });
};

onMounted(() => {
  // 仅在PC端启用（屏幕宽度 > 1024px）
  if (window.innerWidth > 1024) {
    isEnabled.value = true;
    
    // 初始化位置
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    ringX = mouseX = rippleX = centerX;
    ringY = mouseY = rippleY = centerY;
    
    // 添加事件监听
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    
    // 启动动画
    animate();
    
    // 延迟添加悬停监听（等待DOM渲染完成）
    setTimeout(addHoverListeners, 500);
    
    // 隐藏默认光标
    document.body.style.cursor = 'none';
  }
});

onUnmounted(() => {
  // 清理事件监听
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mousedown', handleMouseDown);
  document.removeEventListener('mouseup', handleMouseUp);
  removeHoverListeners();
  
  // 停止动画
  if (animationId) {
    cancelAnimationFrame(animationId);
  }
  
  // 恢复默认光标
  document.body.style.cursor = '';
});
</script>

<style scoped>
.mouse-follower {
  pointer-events: none;
  z-index: 9999;
}

/* 核心光标点 - 简洁 + 微光 */
.cursor-dot {
  position: fixed;
  width: 6px;
  height: 6px;
  background: #22cbf2;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: 
    transform 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    background 0.25s ease,
    box-shadow 0.25s ease;
  pointer-events: none;
  z-index: 10002;
  box-shadow: 0 0 6px rgba(34, 203, 242, 0.4);
}

/* 圆环光标 */
.cursor-ring {
  position: fixed;
  width: 36px;
  height: 36px;
  border: 1.5px solid rgba(34, 203, 242, 0.5);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 10001;
  transition: 
    width 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    height 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.3s ease,
    background 0.3s ease;
}

/* 水波纹容器 */
.ripple-container {
  position: fixed;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 10000;
  transition: left 0.12s ease-out, top 0.12s ease-out;
}

/* 水波纹扩散动画 */
.ripple {
  position: absolute;
  width: 36px;
  height: 36px;
  border: 1px solid rgba(34, 203, 242, 0.4);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: ripple-spread 2.4s ease-out infinite;
  pointer-events: none;
}

@keyframes ripple-spread {
  0% {
    width: 36px;
    height: 36px;
    border-color: rgba(34, 203, 242, 0.4);
  }
  100% {
    width: 100px;
    height: 100px;
    border-color: rgba(34, 203, 242, 0);
  }
}

/* ========== 悬停状态 ========== */

.cursor-dot.cursor-hover {
  transform: translate(-50%, -50%) scale(2);
  background: #f59e0b;
  box-shadow: 0 0 10px rgba(245, 158, 11, 0.5);
}

.cursor-ring.cursor-hover {
  width: 52px;
  height: 52px;
  border-color: rgba(245, 158, 11, 0.6);
  background: rgba(245, 158, 11, 0.05);
}

/* 悬停时水波纹变为橙色 + 加速 */
.ripple-container.cursor-hover .ripple {
  border-color: rgba(245, 158, 11, 0.4);
  animation: ripple-spread-hover 1.5s ease-out infinite;
}

@keyframes ripple-spread-hover {
  0% {
    width: 52px;
    height: 52px;
    border-color: rgba(245, 158, 11, 0.4);
  }
  100% {
    width: 120px;
    height: 120px;
    border-color: rgba(245, 158, 11, 0);
  }
}

/* ========== 点击状态 ========== */

.cursor-dot.cursor-click {
  transform: translate(-50%, -50%) scale(0.5) !important;
  transition-duration: 0.08s;
}

.cursor-ring.cursor-click {
  width: 28px !important;
  height: 28px !important;
  border-color: rgba(34, 203, 242, 0.8) !important;
  transition-duration: 0.08s;
}

/* ========== 移动端隐藏 ========== */
@media (max-width: 1024px) {
  .mouse-follower {
    display: none !important;
  }
}
</style>
